import { useLocation } from 'react-router'

import { pages } from '~/app/routes'
import { SelectOption, SidebarDepartmentSelect } from '~/astra'
import { useStructuralDiff } from '~/hooks/useStructuralDiff'
import { DepartmentKey } from '~/store/slices/filterSlice'
import { useStore } from '~/store/store'
import { selectSpecialities, useDepartments } from '~/storm'
import { trackUserEvent } from '~/utils/analytics'
import { pick } from '~/utils/object'

function useDepartmentSelectorData() {
    const departmentKey = useStore(state => state.appFilters.departmentKey)
    const departments = useDepartments()

    return useStructuralDiff({
        departments: departments.map(dept => pick(dept, ['id', 'name', 'uuid'])),
        selectedDepartmentKey: departmentKey,
    })
}

export function useDepartmentAndSpecialtySelection() {
    const departments = useDepartments()
    const { setDepartment, setSelectedSpecialties } = useStore(state => state.appFilters.actions)

    const selectDepartment = (selectedDepartmentKey: DepartmentKey) => {
        const selectedDepartment = selectedDepartmentKey !== 'all' ? departments.find(d => d.id === selectedDepartmentKey) : undefined
        const name = selectedDepartment ? selectedDepartment.name : 'All'

        void trackUserEvent('DEPARTMENT_SELECTED', {
            departmentName: name,
        })
        setDepartment(selectedDepartmentKey)
        if (selectedDepartmentKey === 'all') {
            setSelectedSpecialties('all')
            return
        }
        const specialties = selectSpecialities(useStore.getState(), { where: { department_uuid: selectedDepartment?.uuid } })
        setSelectedSpecialties(specialties.map(specialty => specialty.uuid))
    }

    return { departments, selectDepartment }
}

export const DepartmentSelect = () => {
    const { pathname } = useLocation()
    const { departments, selectedDepartmentKey } = useDepartmentSelectorData()
    const { selectDepartment } = useDepartmentAndSpecialtySelection()

    const departmentOptions: SelectOption<DepartmentKey>[] = [
        { value: 'all', label: 'Alle' },
        ...departments.map(({ name, id }) => ({ value: id, label: name })),
    ]
    const selectedDepartment = departmentOptions.find(option => option.value === selectedDepartmentKey)

    const usesDepartmentFilter = !pages.DOWNLOAD.views.download.path.startsWith(pathname)

    if (!usesDepartmentFilter || !departments.length) {
        return null
    }

    return <SidebarDepartmentSelect options={departmentOptions} selected={selectedDepartment} onSelect={selectDepartment} />
}
